import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import fleetApi, { fleetError } from "@/lib/fleetApi";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/brand/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowLeft, Smartphone, CheckCircle2 } from "lucide-react";

const field = "mt-1.5 border-slate-700 bg-slate-900 text-white placeholder:text-slate-500 focus-visible:ring-blue-600";
const ROLE = { driver: "Driver", dispatcher: "Dispatcher", supervisor: "Supervisor", fleet_manager: "Fleet Manager" };

export default function JoinPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [params] = useSearchParams();
  const [code, setCode] = useState((params.get("code") || "").toUpperCase());
  const [invite, setInvite] = useState(null);
  const [step, setStep] = useState("code");
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ full_name: "", phone: "", password: "", confirm: "" });
  const [verifyCode, setVerifyCode] = useState("");
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const lookup = async (c = code) => {
    if (!c.trim()) return;
    setBusy(true);
    try {
      const { data } = await fleetApi.get(`/invites/lookup`, { params: { code: c.trim().toUpperCase() } });
      setInvite(data);
      setF((p) => ({ ...p, full_name: data.full_name || "", phone: data.phone || "" }));
      setStep("details");
    } catch (err) {
      toast.error(fleetError(err, "That invite code isn't valid."));
    } finally { setBusy(false); }
  };

  useEffect(() => {
    if (params.get("code")) { lookup(params.get("code")); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const start = async (e) => {
    e?.preventDefault();
    if (f.password.length < 8) { toast.error("Password must be at least 8 characters."); return; }
    if (f.password !== f.confirm) { toast.error("Passwords don't match."); return; }
    setBusy(true);
    try {
      await fleetApi.post("/auth/join/start", { code: code.trim().toUpperCase(), full_name: f.full_name, phone: f.phone, password: f.password });
      setStep("verify");
      toast.success(`We sent a verification code to ${invite.email}.`);
    } catch (err) {
      toast.error(fleetError(err, "Couldn't join with this code."));
    } finally { setBusy(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await fleetApi.post("/auth/join/verify", { code: code.trim().toUpperCase(), verify_code: verifyCode.trim() });
      if (invite.role === "driver") { setStep("done"); return; }
      const res = await login(invite.email, f.password);
      if (!res.ok) { setStep("done"); return; }
      toast.success(`Welcome to the team, ${f.full_name.split(" ")[0] || ""}!`);
      navigate("/portal/dashboard", { replace: true });
    } catch (err) {
      toast.error(fleetError(err, "That code didn't work."));
    } finally { setBusy(false); }
  };

  const btn = "w-full gap-2 rounded-lg bg-blue-600 py-6 text-base font-semibold text-white hover:bg-blue-500";
  return (
    <div className="flex min-h-screen items-start justify-center bg-slate-950 p-6 lg:items-center" data-testid="join-page">
      <div className="w-full max-w-md py-8">
        <Link to="/login" className="mb-6 flex items-center gap-2 text-sm font-medium text-slate-400 hover:text-white" data-testid="join-back">
          <ArrowLeft className="h-4 w-4" /> Back to sign in
        </Link>
        <Logo variant="light" />
        {step === "code" && (
          <form onSubmit={(e) => { e.preventDefault(); lookup(); }} className="mt-6 space-y-5">
            <h1 className="font-display text-3xl font-bold text-white">Join your team</h1>
            <p className="text-slate-400">Enter the invite code from your email.</p>
            <div>
              <Label htmlFor="code" className="text-slate-300">Invite code</Label>
              <Input id="code" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} required placeholder="AB12CD34"
                data-testid="join-code" className={`${field} text-center font-mono-data text-lg tracking-[0.3em]`} />
            </div>
            <Button type="submit" disabled={busy} className={btn} data-testid="join-lookup">{busy ? <Loader2 className="h-5 w-5 animate-spin" /> : null} Continue</Button>
          </form>
        )}
        {step === "details" && invite && (
          <form onSubmit={start} className="mt-6 space-y-4">
            <h1 className="font-display text-3xl font-bold text-white">Create your login</h1>
            <p className="text-slate-400" data-testid="join-invite-info">
              You're joining as <span className="font-semibold text-white">{ROLE[invite.role] || invite.role}</span> with <span className="font-semibold text-white">{invite.email}</span>.
            </p>
            {[["full_name", "Full name", "text", true], ["phone", "Phone", "tel", false], ["password", "Password", "password", true], ["confirm", "Confirm password", "password", true]].map(([k, l, t, req]) => (
              <div key={k}>
                <Label htmlFor={k} className="text-slate-300">{l}</Label>
                <Input id={k} type={t} value={f[k]} onChange={set(k)} required={req} data-testid={`join-${k}`} className={field} placeholder={k === "password" ? "At least 8 characters" : ""} />
              </div>
            ))}
            <Button type="submit" disabled={busy} className={btn} data-testid="join-submit">{busy ? <Loader2 className="h-5 w-5 animate-spin" /> : null} Join team</Button>
          </form>
        )}
        {step === "verify" && (
          <form onSubmit={verify} className="mt-6 space-y-5">
            <h1 className="font-display text-3xl font-bold text-white">Check your email</h1>
            <p className="text-slate-400">Enter the verification code we sent to <span className="font-semibold text-white">{invite?.email}</span>.</p>
            <Input value={verifyCode} onChange={(e) => setVerifyCode(e.target.value)} required autoFocus inputMode="numeric" placeholder="123456"
              data-testid="join-verify-code" className={`${field} text-center font-mono-data text-xl tracking-[0.4em]`} />
            <Button type="submit" disabled={busy || !verifyCode.trim()} className={btn} data-testid="join-verify">{busy ? <Loader2 className="h-5 w-5 animate-spin" /> : null} Verify</Button>
            <button type="button" onClick={start} disabled={busy} className="text-sm text-blue-400 hover:underline" data-testid="join-resend">Resend code</button>
          </form>
        )}
        {step === "done" && (
          <div className="mt-6 rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-6" data-testid="join-done">
            <CheckCircle2 className="h-8 w-8 text-emerald-400" />
            <h1 className="mt-3 font-display text-2xl font-bold text-white">You're on the team</h1>
            <p className="mt-2 text-sm text-slate-300">
              {invite?.role === "driver" ? (
                <><Smartphone className="mr-1 inline h-4 w-4" /> Download the RideStamp driver app and sign in with <span className="font-semibold text-white">{invite?.email}</span> and the password you just created.</>
              ) : <>Sign in with <span className="font-semibold text-white">{invite?.email}</span> on the web portal or the RideStamp app.</>}
            </p>
            <Button onClick={() => navigate("/login")} className="mt-5 bg-blue-700 text-white hover:bg-blue-800" data-testid="join-go-login">Go to sign in</Button>
          </div>
        )}
      </div>
    </div>
  );
}
